import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  Plus,
  Trash2,
  Save,
  Calculator,
  User,
  Building2,
  Loader2,
  Link as LinkIcon,
  Copy,
  Check
} from 'lucide-react';

import Layout from '../components/Layout';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import { invoiceAPI, clientAPI } from '../services/api';
import { supplierAPI } from '../services/supplierApi';
import {
  InvoiceRequest,
  OrderItem,
  Client,
  Supplier,
  Company,
  Invoice,
  PaymentMethod,
  InvoiceType,
  Currency,
  Product
} from '../types';
import OrderItemModal from '../components/OrderItemModal';
import { getErrorMessage } from '../utils/errorHandler';
import { calculateTotalInWords, Language } from '../utils/numberToWords';
import { productAPI } from "../services/productApi";
import { setEncryptionEnabled } from '../utils/EncryptionUtils';

const InvoiceEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { selectedCompany } = useCompany();
  const { t, isRTL, currentLanguage } = useI18n();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null);
  const [originalInvoice, setOriginalInvoice] = useState<Invoice | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [discountAmount, setDiscountAmount] = useState<number>(0);

  const [formData, setFormData] = useState<InvoiceRequest>({
    issuerId: selectedCompany.selectedCompanyId || '',
    clientId: '',
    invoiceNumber: undefined,
    creationDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    currency: 'MAD',
    invoiceType: 'SELL_INVOICE',
    paymentMethod: 'CASH',
    totalInWords: '',
    language: 'fr',
    order: {
      items: [],
      vatRate: 20,
      discount: 0,
      discountAmount: 0
    }
  });

  // Load initial data and invoice
  useEffect(() => {
    if (selectedCompany.selectedCompanyId && invoiceId && selectedCompany.selectedCompany) {
      // Ensure encryption is enabled if the company has it
      setEncryptionEnabled(!!selectedCompany.selectedCompany.encryptionFeatureEnabled);
      setPaymentMethods(Object.values(PaymentMethod));
      loadInvoice();
      loadProducts()
    }
  }, []);

  // Auto-update totalInWords when orderItems, discount, or VAT rate changes
  useEffect(() => {
    if (formData.order?.items && formData.order.items.length > 0) {
      const calculatedTotalInWords = calculateTotalInWordsLocal();
      // Always update the totalInWords when calculations change
      setFormData(prev => ({ ...prev, totalInWords: calculatedTotalInWords }));
    }
  }, [formData.order?.items, formData.order?.discount, formData.order?.vatRate, formData.currency, currentLanguage]);


  const loadProducts = async () => {
    if (!selectedCompany.selectedCompanyId) return;
    const productsData = await productAPI.getProductsByCompany(selectedCompany.selectedCompanyId);
    setProducts(productsData);
  };

  const loadInvoice = async () => {
    if (!invoiceId) return;

    try {
      setInitialLoading(true);
      const invoice = await invoiceAPI.getInvoiceById(invoiceId);
      setOriginalInvoice(invoice);

      // Convert invoice to form data
      setFormData({
        issuerId: invoice.issuerId,
        clientId: invoice.clientId,
        invoiceNumber: invoice.invoiceNumber,
        creationDate: invoice.creationDate ? new Date(invoice.creationDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        currency: invoice.currency,
        invoiceType: invoice.invoiceType,
        paymentMethod: invoice.paymentMethod,
        totalInWords: invoice.totalInWords || '',
        language: invoice.language || 'fr',
        deadlineDate: invoice.deadlineDate,
        order: {
          items: invoice.order?.items?.map(item => ({
            id: item.id,
            companyId: item.companyId,
            productId: item.productId,
            code: item.code,
            description: item.description,
            unitPrice: item.unitPrice,
            unity: item.unity,
            quantity: item.quantity
          })) || [],
          vatRate: invoice.order?.vatRate || 20,
          discount: invoice.order?.discount || 0,
          discountAmount: invoice.order?.discountAmount || 0
        }
      });
      loadClientsOrSuppliers(invoice.invoiceType);
      // Set discountAmount state from loaded invoice
      setDiscountAmount(invoice.order?.discountAmount || 0);
    } catch (err: any) {
      console.error('Error loading invoice:', err);
      setError(getErrorMessage(err));
    } finally {
      setInitialLoading(false);
    }
  };

  const loadClientsOrSuppliers = async (invoiceType: string) => {
    if (!selectedCompany.selectedCompanyId) return;
    if(invoiceType === InvoiceType.PURCHASE_INVOICE){
      const suppliersData = await supplierAPI.getSuppliersByCompany(selectedCompany.selectedCompanyId);
      setSuppliers(suppliersData);
    }else {
      const clientsData = await clientAPI.getClientsByCompany(selectedCompany.selectedCompanyId);
      setClients(clientsData);
    }
  };

  const handleInputChange = (field: keyof InvoiceRequest, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOrderFieldChange = (field: keyof typeof formData.order, value: any) => {
    setFormData(prev => ({
      ...prev,
      order: {
        ...prev.order,
        [field]: value
      }
    }));
  };

  const handleProductSave = (item: OrderItem) => {
    if (editingProductIndex !== null) {
      // Update existing product
      setFormData(prev => ({
        ...prev,
        order: {
          ...prev.order,
          items: prev.order.items?.map((p, index) =>
            index === editingProductIndex ? item : p
          ) || []
        }
      }));
      setEditingProductIndex(null);
    } else {
      // Add new product
      setFormData(prev => ({
        ...prev,
        order: {
          ...prev.order,
          items: [...(prev.order.items || []), item]
        }
      }));
    }
    setShowProductModal(false);
  };

  const handleProductEdit = (index: number) => {
    setEditingProductIndex(index);
    setShowProductModal(true);
  };

  const handleProductDelete = (index: number) => {
    setFormData(prev => ({
      ...prev,
      order: {
        ...prev.order,
        items: prev.order.items?.filter((_, i) => i !== index) || []
      }
    }));
  };

  const calculateSubtotal = () => {
    return formData.order.items?.reduce((sum, item) => {
      const total = Math.round((item.unitPrice * item.quantity) * 100) / 100;
      return sum + total;
    }, 0) || 0;
  };

  const calculateDiscountAmount = () => {
    // Use discountAmount directly as the source of truth
    return Math.round((discountAmount || 0) * 100) / 100;
  };

  const calculateTotalAfterDiscount = () => {
    const subtotal = calculateSubtotal();
    const discountAmountVal = calculateDiscountAmount();
    return Math.round((subtotal - discountAmountVal) * 100) / 100;
  };

  const calculateVAT = () => {
    const totalAfterDiscount = calculateTotalAfterDiscount();
    return Math.round((totalAfterDiscount * (formData.order.vatRate || 0) / 100) * 100) / 100;
  };

  const calculateTotal = () => {
    const totalAfterDiscount = calculateTotalAfterDiscount();
    const vat = calculateVAT();
    return Math.round((totalAfterDiscount + vat) * 100) / 100;
  };

  // Function to convert number to words (simplified version)
  const calculateTotalInWordsLocal = () => {
    const total = calculateTotal();
    const language = currentLanguage as Language;
    return calculateTotalInWords(total, formData.currency, language);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invoiceId) {
      setError('Invoice ID is missing');
      return;
    }

    if (!formData.clientId) {
      const errorMsg = formData.invoiceType === InvoiceType.PURCHASE_INVOICE
        ? t('invoices.selectSupplier') || 'Please select a supplier'
        : t('invoiceEdit.selectClient') || 'Please select a client';
      setError(errorMsg);
      return;
    }

    if (!formData.order.items || formData.order.items.length === 0) {
      setError('Please add at least one product');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare the data with totalInWords
      const updatedFormData: InvoiceRequest = {
        ...formData,
        totalInWords: formData.totalInWords || calculateTotalInWordsLocal(),
        order: {
          ...formData.order,
          discountAmount: discountAmount
        }
      };

      await invoiceAPI.updateInvoice(invoiceId, updatedFormData);
      navigate('/invoices');
    } catch (err: any) {
      console.error('Error updating invoice:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const formatInvoiceNumberWithDate = () => {
    if (!formData.invoiceNumber || !formData.creationDate) return '';

    const date = new Date(formData.creationDate);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${formData.invoiceNumber.toString().padStart(6, '0')}/${day}${month}${year}`;
  };

  // Show loading state while loading invoice
  if (initialLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">{t('invoiceEdit.loadingInvoice')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Show error if invoice not found
  if (error && !originalInvoice) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <DollarSign className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('invoiceEdit.errorLoadingInvoice')}</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => navigate('/invoices')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('invoiceEdit.backToInvoices')}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/invoices')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <span>{t('invoiceEdit.backToInvoices')}</span>
            </button>
          </div>
          <div className="flex items-center space-x-3">
            <DollarSign className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">{t('invoiceEdit.editInvoice')}</h1>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Invoice Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Calculator className="w-5 h-5 mr-2" />
              {t('invoiceEdit.invoiceDetails')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Invoice Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEdit.invoiceNumber')} *
                </label>
                <input
                  type="number"
                  required
                  value={formData.invoiceNumber?.toString() || ''}
                  onChange={(e) => handleInputChange('invoiceNumber', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('invoiceEdit.invoiceNumberPlaceholder')}
                />
              </div>

              {/* Creation Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEdit.creationDate')} *
                </label>
                <input
                  type="date"
                  required
                  value={formData.creationDate || ''}
                  onChange={(e) => handleInputChange('creationDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {formData.invoiceNumber && formData.creationDate && (
                  <p className="mt-1 text-sm text-gray-500">
                    {t('invoices.formattedReference') || 'Formatted'}: {formatInvoiceNumberWithDate()}
                  </p>
                )}
              </div>

              {/* Deadline Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.deadlineDate')}
                </label>
                <input
                  type="date"
                  value={formData.deadlineDate || ''}
                  onChange={(e) => handleInputChange('deadlineDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Client/Supplier Selection */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.invoiceType === InvoiceType.PURCHASE_INVOICE
                    ? t('invoices.supplier')
                    : t('invoiceEdit.client')} *
                </label>
                <select
                  value={formData.clientId}
                  onChange={(e) => handleInputChange('clientId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">
                    {formData.invoiceType === InvoiceType.PURCHASE_INVOICE
                      ? t('invoices.selectSupplier')
                      : t('invoiceEdit.selectClient')}
                  </option>
                  {formData.invoiceType === InvoiceType.PURCHASE_INVOICE
                    ? suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name} {supplier.ice && `(${supplier.ice})`}
                      </option>
                    ))
                    : clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} {client.ice && `(${client.ice})`}
                      </option>
                    ))
                  }
                </select>
              </div>

              {/* Invoice Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEdit.invoiceType')} *
                </label>
                <select
                  value={formData.invoiceType}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value={formData.invoiceType}>
                    {t(`invoiceEdit.types.${formData.invoiceType}`)}
                  </option>
                </select>
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEdit.currency')} *
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {Object.values(Currency).map((currency) => (
                    <option key={currency} value={currency}>
                      {currency} – {t('companies.currency'+currency.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEdit.paymentMethod')} *
                </label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {t(`common.payments.${method}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* VAT Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEdit.vatRate')} (%) *
                </label>
                <input
                  type="number"
                  value={formData.order.vatRate}
                  onChange={(e) => handleOrderFieldChange('vatRate', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                />
              </div>

              {/* Discount Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.discountPercentage')}
                </label>
                <input
                  type="number"
                  value={formData.order.discount || 0}
                  onChange={(e) => {
                    const percentage = parseFloat(e.target.value) || 0;
                    handleOrderFieldChange('discount', percentage);
                    // Calculate value from percentage
                    const subtotal = calculateSubtotal();
                    const value = Math.round((subtotal * percentage / 100) * 100) / 100;
                    setDiscountAmount(value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>

              {/* Discount Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.discountAmount')}
                </label>
                <input
                  type="number"
                  value={discountAmount}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setDiscountAmount(value);
                    // Calculate percentage from value
                    const subtotal = calculateSubtotal();
                    const percentage = subtotal > 0 ? Math.round((value / subtotal * 100) * 100) / 100 : 0;
                    handleOrderFieldChange('discount', Math.min(percentage, 100));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* OrderItems Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Plus className="w-5 h-5 mr-2" />
                {t('invoiceEdit.products')}
              </h2>
              <button
                type="button"
                onClick={() => setShowProductModal(true)}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t('invoiceEdit.addProduct')}</span>
              </button>
            </div>

            {formData.order.items && formData.order.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceEdit.code')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceEdit.description')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceEdit.price')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceEdit.unit')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceEdit.quantity')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceEdit.total')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoiceEdit.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.order.items.map((product, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {product.code || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {product.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {product.unitPrice.toFixed(2)} {formData.currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {product.unity || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {product.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {(product.unitPrice * product.quantity).toFixed(2)} {formData.currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => handleProductEdit(index)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              {t('invoiceEdit.edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleProductDelete(index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Plus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t('invoiceEdit.noProductsAdded')}</p>
              </div>
            )}
          </div>

          {/* Totals Section */}
          {formData.order.items && formData.order.items.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <Calculator className="w-5 h-5 mr-2" />
                {t('invoiceEdit.invoiceSummary')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('invoiceEdit.subtotal')}:</span>
                    <span className="font-medium">{calculateSubtotal().toFixed(2)} {formData.currency}</span>
                  </div>
                  {formData.order.discount && formData.order.discount > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('invoiceEdit.discount')} ({formData.order.discount}%):</span>
                        <span className="font-medium text-red-600">-{calculateDiscountAmount().toFixed(2)} {formData.currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('invoiceEdit.totalAfterDiscount')}:</span>
                        <span className="font-medium">{calculateTotalAfterDiscount().toFixed(2)} {formData.currency}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('invoiceEdit.vat')} ({formData.order.vatRate}%):</span>
                    <span className="font-medium">{calculateVAT().toFixed(2)} {formData.currency}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold border-t pt-4">
                    <span>{t('invoiceEdit.total')}:</span>
                    <span className="text-blue-600">{calculateTotal().toFixed(2)} {formData.currency}</span>
                  </div>
                </div>

                {/* Total in Words */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('invoiceEdit.totalInWords')}
                  </label>
                  <input
                    type="text"
                    value={formData.totalInWords}
                    onChange={(e) => handleInputChange('totalInWords', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('invoiceEdit.totalInWordsPlaceholder')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('invoiceEdit.totalInWordsDescription')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/invoices')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('invoiceEdit.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? t('invoiceEdit.updating') : t('invoiceEdit.updateInvoice')}</span>
            </button>
          </div>
        </form>

        {/* Product Modal */}
        {showProductModal && (
          <OrderItemModal
            isOpen={showProductModal}
            onClose={() => {
              setShowProductModal(false);
              setEditingProductIndex(null);
            }}
            currency={formData.currency}
            onSave={handleProductSave}
            item={editingProductIndex !== null ? {
              id: formData.order.items?.[editingProductIndex]?.id,
              companyId: selectedCompany.selectedCompanyId || '',
              code: formData.order.items?.[editingProductIndex]?.code || '',
              description: formData.order.items?.[editingProductIndex]?.description || '',
              unitPrice: formData.order.items?.[editingProductIndex]?.unitPrice || 0,
              unity: formData.order.items?.[editingProductIndex]?.unity || '',
              quantity: formData.order.items?.[editingProductIndex]?.quantity || 1
            } : {
              companyId: selectedCompany.selectedCompanyId || '',
              code: '',
              description: '',
              unitPrice: 0,
              unity: '',
              quantity: 1
            }}
            mode={editingProductIndex !== null ? 'edit' : 'create'}
            products={products}
          />
        )}
      </div>
    </Layout>
  );
};

export default InvoiceEditPage;
